import { appConfig, callable } from "../../db.js";

const WATCHERS_PATH = "NotificationManager/Watchers";

const BLOCKED_SOURCE_PREFIXES = ["Notifications", "NotificationManager"];

const MAX_CONDITIONS = 3;

function validateSourcePath(path) {
    if (!path) return false;
    const first = path.split("/")[0];
    return !BLOCKED_SOURCE_PREFIXES.includes(first);
}

const TYPE_OPTIONS = [
    { label: "Info", id: "Info" },
    { label: "Warn", id: "Warn" },
    { label: "Error", id: "Error" },
];

const TRIGGER_OPTIONS = [
    { label: "Event (on data change)", id: "event" },
    { label: "Cron (scheduled)", id: "cron" },
];

const FREQUENCY_OPTIONS = [
    { label: "Every 15 Minutes", id: "Every15Minutes" },
    { label: "Daily", id: "Daily" },
    { label: "Weekly", id: "Weekly" },
    { label: "Monthly", id: "Monthly" },
];

const RECIPIENT_MODE_OPTIONS = [
    { label: "Static users", id: "static" },
    { label: "Dynamic field", id: "dynamic" },
    { label: "Static users and dynamic field", id: "both" },
];

const DELIVERY_MODE_OPTIONS = [
    { label: "Every matching run", id: "everyRun" },
    { label: "Once per entity", id: "once" },
    { label: "Once per entity per day", id: "daily" },
];

const DAY_OF_WEEK_OPTIONS = [
    { label: "Sunday", id: 0 },
    { label: "Monday", id: 1 },
    { label: "Tuesday", id: 2 },
    { label: "Wednesday", id: 3 },
    { label: "Thursday", id: 4 },
    { label: "Friday", id: 5 },
    { label: "Saturday", id: 6 },
];

const OPERATOR_OPTIONS = [
    { label: "None", id: "" },
    { label: "Equals", id: "eq" },
    { label: "Not Equals", id: "neq" },
    { label: "Exists", id: "exists" },
    { label: "Missing or False", id: "missing" },
    { label: "Became", id: "became" },
    { label: "Days Until (date)", id: "daysUntil" },
    { label: "Overdue (date)", id: "overdue" },
    { label: "Due Within (date)", id: "within" },
    { label: "Minutes Until", id: "minutesUntil" },
    { label: "Minutes Overdue", id: "minutesOverdue" },
];

const isCron = (df) => df.liveInputData.trigger === "cron";
const isEvent = (df) => df.liveInputData.trigger !== "cron";
const isWeekly = (df) => isCron(df) && df.liveInputData["schedule[frequency]"] === "Weekly";
const isMonthly = (df) => isCron(df) && df.liveInputData["schedule[frequency]"] === "Monthly";
const usesStaticRecipients = (df) => ["static", "both"].includes(df.liveInputData.recipientMode || "static");
const usesDynamicRecipients = (df) => ["dynamic", "both"].includes(df.liveInputData.recipientMode);

/**
 * Builds the shared field list used by both add and edit forms.
 * Event-only and cron-only fields toggle via their `show` predicate.
 */
function buildWatcherFields() {
    const conditionFields = [];
    for (let i = 0; i < MAX_CONDITIONS; i++) {
        conditionFields.push(
            {
                label: `Condition ${i + 1} Field`, name: `conditions[${i}][field]`, type: "text",
                required: false, show: isCron, divClass: "md:col-span-1",
            },
            {
                label: `Condition ${i + 1} Operator`, name: `conditions[${i}][operator]`, type: "dropdown",
                required: false, show: isCron, dropdownData: OPERATOR_OPTIONS, divClass: "md:col-span-1",
            },
            {
                label: `Condition ${i + 1} Value`, name: `conditions[${i}][value]`, type: "text",
                required: false, show: isCron, divClass: "md:col-span-1",
            },
        );
    }

    return [
        { label: "Type", name: "type", type: "dropdown", dropdownData: TYPE_OPTIONS },
        { label: "Trigger", name: "trigger", type: "dropdown", dropdownData: TRIGGER_OPTIONS },
        { label: "Title", name: "title", type: "text", divClass: "md:col-span-3" },
        { label: "Body", name: "body", type: "text", divClass: "md:col-span-3" },
        {
            label: "Source Path", name: "sourcePath", type: "text", divClass: "md:col-span-3",
            tooltip: "Event: path pattern to watch (supports * and **). Cron: collection path to iterate, e.g. Audits/NonConformities",
        },
        { label: "Target Path", name: "targetPath", type: "text", divClass: "md:col-span-3" },
        { label: "Enabled", name: "enabled", type: "toggle" },
        { label: "Recipient Mode", name: "recipientMode", type: "dropdown", dropdownData: RECIPIENT_MODE_OPTIONS },
        {
            label: "User IDs (comma separated)", name: "userIds", type: "dropdown", dataLocation: "users",
            labelKey: "name", multiple: true, idKey: "key", divClass: "md:col-span-3", required: false,
            show: usesStaticRecipients,
        },
        {
            label: "Dynamic Recipient Field", name: "dynamicRecipientField", type: "text", required: false,
            show: usesDynamicRecipients,
            tooltip: "Dot path to a Firebase Auth UID, UID array, or UID-keyed map, e.g. AuthUID, Owner.AuthUID, or Reviewers",
        },
        { label: "Action Route", name: "actionRoute", type: "text", required: false, divClass: "md:col-span-2" },
        { label: "Action Label", name: "actionLabel", type: "text", required: false },
        { label: "Delivery Mode", name: "deliveryMode", type: "dropdown", dropdownData: DELIVERY_MODE_OPTIONS },

        // --- Event filter (single condition) ---
        { label: "Filter Field", name: "filters[field]", type: "text", required: false, show: isEvent },
        {
            label: "Filter Operator", name: "filters[operator]", type: "dropdown", required: false,
            show: isEvent, dropdownData: OPERATOR_OPTIONS,
        },
        { label: "Filter Value", name: "filters[value]", type: "text", required: false, show: isEvent },

        // --- Cron schedule ---
        {
            label: "Frequency", name: "schedule[frequency]", type: "dropdown", required: false,
            show: isCron, dropdownData: FREQUENCY_OPTIONS, "divClass": "md:col-span-3",
        },
        {
            label: "Day of Week", name: "schedule[dayOfWeek]", type: "dropdown", required: false,
            show: isWeekly, dropdownData: DAY_OF_WEEK_OPTIONS,
        },
        {
            label: "Day of Month", name: "schedule[dayOfMonth]", type: "number", required: false,
            show: isMonthly, min: 1, max: 31,
        },

        // --- Cron conditions (combined with AND) ---
        ...conditionFields,
    ];
}

function normalizeConditions(raw) {
    if (!raw) return null;
    const list = Array.isArray(raw) ? raw : Object.values(raw);
    const cleaned = list
        .filter((c) => c && c.field && c.operator)
        .map((c) => ({
            field: c.field,
            operator: c.operator,
            value: ["exists", "missing", "overdue"].includes(c.operator) ? true : (c.value ?? ""),
        }));
        
    return cleaned.length ? cleaned : null;
}

/**
 * Transforms serialized form data into the watcher document shape.
 * Shared by add and edit; `isEdit` controls whether createdAt is stamped.
 */
function buildWatcherData(data, { isEdit }) {
    if (!validateSourcePath(data.sourcePath)) {
        throw new Error("Source path cannot target Notifications or NotificationManager routes.");
    }

    if (data.userIds && typeof data.userIds === "string") {
        data.userIds = data.userIds.split(",").map((id) => id.trim()).filter(Boolean);
    }

    const recipientMode = data.recipientMode || "static";
    if (["dynamic", "both"].includes(recipientMode) && !String(data.dynamicRecipientField || "").trim()) {
        throw new Error("Enter a dynamic recipient field for this recipient mode.");
    }

    const base = {
        trigger: data.trigger || "event",
        targetPath: data.targetPath || "Notifications",
        enabled: data.enabled === "true" || data.enabled === true,
        recipientMode,
        dynamicRecipientField: String(data.dynamicRecipientField || "").trim() || null,
        actionRoute: String(data.actionRoute || "").trim() || null,
        actionLabel: String(data.actionLabel || "").trim() || null,
        deliveryMode: data.deliveryMode || "everyRun",
        updatedAt: Date.now(),
    };
    if (!isEdit) {
        base.createdAt = Date.now();
    }

    if (data.trigger === "cron") {
        const frequency = data.schedule?.frequency || "Daily";
        const schedule = { frequency };
        if (frequency === "Weekly") schedule.dayOfWeek = Number(data.schedule?.dayOfWeek ?? 1);
        if (frequency === "Monthly") schedule.dayOfMonth = Number(data.schedule?.dayOfMonth ?? 1);

        return { ...base, schedule, conditions: normalizeConditions(data.conditions), filters: null };
    }

    const filters = data.filters?.operator
        ? { field: data.filters.field || "", operator: data.filters.operator, value: data.filters.operator === "exists" ? true : data.filters.value || "" }
        : null;

    return { ...base, filters, schedule: null, conditions: null };
}

export const addWatcher = ({ form }) => {
    form.setValues({
        type: "Info",
        trigger: "event",
        targetPath: "Notifications",
        enabled: true,
        recipientMode: "static",
        deliveryMode: "everyRun",
        "schedule[frequency]": "Daily",
    });
    form.setConfig({
        title: "Add Watcher",
        type: "push",
        location: WATCHERS_PATH,
        fields: buildWatcherFields(),
        preSubmitHook({ data }) {
            return buildWatcherData(data, { isEdit: false });
        },
    });
    form.getDropdownData();
    form.toggle();
};

export const editWatcher = ({ form, item }) => {
    const pageItem = item;
    const setValues = {
        ...pageItem,
        trigger: pageItem?.trigger || "event",
        recipientMode: pageItem?.recipientMode || "static",
        deliveryMode: pageItem?.deliveryMode || "everyRun",
        "filters[field]": pageItem?.filters?.field || "",
        "filters[operator]": pageItem?.filters?.operator || "",
        "filters[value]": pageItem?.filters?.value || "",
        "schedule[frequency]": pageItem?.schedule?.frequency || "Daily",
        "schedule[dayOfWeek]": pageItem?.schedule?.dayOfWeek ?? "",
        "schedule[dayOfMonth]": pageItem?.schedule?.dayOfMonth ?? "",
    };
    for (let i = 0; i < MAX_CONDITIONS; i++) {
        const c = pageItem?.conditions?.[i];
        setValues[`conditions[${i}][field]`] = c?.field || "";
        setValues[`conditions[${i}][operator]`] = c?.operator || "";
        setValues[`conditions[${i}][value]`] = c?.value === true ? "" : (c?.value ?? "");
    }

    form.setValues(setValues);
    form.setConfig({
        title: "Edit Watcher",
        type: "update",
        location: `${WATCHERS_PATH}/${pageItem?.ID}`,
        set: true,
        fields: buildWatcherFields(),
        preSubmitHook({ data }) {
            return buildWatcherData(data, { isEdit: true });
        },
    });
    form.getDropdownData();
    form.toggle();
};

export const deleteWatcher = ({ form, pageItem }) => {
    form.setConfig({
        title: "Delete Watcher",
        description: `<p>Are you sure you want to delete this watcher?</p>
            <p class="font-medium mt-2">Type: ${pageItem.type || "—"}</p>
            <p>Source: ${pageItem.sourcePath || "—"}</p>`,
        type: "delete",
        location: `${WATCHERS_PATH}/${pageItem.ID}`,
        fields: [],
    });
    form.toggle();
};

export const toggleWatcher = ({ db, pageItem }) => {
    db.update(`${WATCHERS_PATH}/${pageItem.ID}`, {
        enabled: !pageItem.enabled,
        updatedAt: Date.now(),
    });
};

export const triggerNowWatcher = ({ form, pageItem, toast }) => {
    if (!pageItem?.ID) {
        toast.error("Unable to trigger watcher: missing watcher ID.");
        return;
    }

    if (pageItem?.trigger !== "cron") {
        toast.error("Trigger now is only available for cron watchers.");
        return;
    }

    if (pageItem?.enabled !== true) {
        toast.error("Enable this watcher before running Trigger Now.");
        return;
    }

    form.setConfig({
        title: "Trigger Watcher Now",
        type: "ignoreAutoAction",
        submitButtonLabel: "Trigger Now",
        description: `<p>This will run this cron watcher immediately.</p>
            <p class="font-medium mt-2">Type: ${pageItem.type || "—"}</p>
            <p>Source: ${pageItem.sourcePath || "—"}</p>`,
        fields: [],
        async postSubmitHook() {
            const triggerCronWatcherNow = callable("triggerCronWatcherNow");
            const result = await triggerCronWatcherNow({
                watcherId: pageItem.ID,
                databaseURL: appConfig?.firebaseConfig?.databaseURL,
            });

            const data = result?.data || {};
            toast.success(`Watcher run complete: ${data.notificationsWritten || 0} notifications from ${data.matched || 0} matched items.`);
        },
    });
    form.toggle();
};
